import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { confirm } from "@/shared/lib/confirm";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatLakDisplay, formatQtyDisplay } from "@/shared/lib/format-number";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CheckIcon,
  PackageIcon,
  PencilIcon,
  PrinterIcon,
  SendIcon,
  XIcon,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { formatPurchaseOrderApiError } from "../api/purchase-orders-client";
import {
  useApprovePurchaseOrder,
  useCancelPurchaseOrder,
  useClosePurchaseOrder,
  usePurchaseOrderQuery,
  useReceivePurchaseOrder,
  useRejectPurchaseOrder,
  useSubmitPurchaseOrder,
} from "../api/purchase-orders-queries";
import { PurchaseOrderReceiveDialog } from "../ui/purchase/PurchaseOrderReceiveDialog";
import { poItemTypeLabel, PO_MENU_TITLE } from "../ui/purchase/purchaseLaoLabels";
import { openPurchaseOrderPrint } from "../ui/purchase/poPrint";
import { PurchaseOrderStatusBadge } from "../ui/purchase/PurchaseOrderStatusBadge";

export function PurchaseOrderDetailPage() {
  const { id } = useParams({ from: "/app/inventory/purchase-orders/$id" });
  const nav = useNavigate();
  const query = usePurchaseOrderQuery(id);
  const submit = useSubmitPurchaseOrder(id);
  const approve = useApprovePurchaseOrder(id);
  const reject = useRejectPurchaseOrder(id);
  const cancel = useCancelPurchaseOrder(id);
  const close = useClosePurchaseOrder(id);
  const receive = useReceivePurchaseOrder(id);

  const canCreate = useActionPermission(["inventory:purchase:create"]);
  const canApprove = useActionPermission(["inventory:purchase:approve"]);
  const canReceive = useActionPermission(["inventory:purchase:receive"]);

  const [receiveOpen, setReceiveOpen] = useState(false);
  const po = query.data;

  const runAction = async (
    label: string,
    fn: () => Promise<unknown>,
  ) => {
    try {
      await fn();
      toast.success(`${label}ສຳເລັດ`);
    } catch (e) {
      toast.error(formatPurchaseOrderApiError(e));
    }
  };

  const runWithConfirm = async (
    options: Parameters<typeof confirm>[0],
    successLabel: string,
    fn: () => Promise<unknown>,
  ) => {
    const ok = await confirm(options);
    if (!ok) return;
    await runAction(successLabel, fn);
  };

  const canReceiveNow =
    po &&
    (po.status === "APPROVED" || po.status === "PARTIALLY_RECEIVED") &&
    po.lines.some((l) => l.qtyOpen > 0);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: PO_MENU_TITLE, to: "/app/inventory/purchase-orders" },
            { label: po?.poNo ?? "…" },
          ]}
        />
      </Header>
      <Main>
        <QueryState result={query} title="ກຳລັງໂຫຼດ" variant="fullscreen">
          {!po ? null : (
            <div className="space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="font-mono font-bold text-2xl tracking-tight">
                      {po.poNo}
                    </h1>
                    <PurchaseOrderStatusBadge status={po.status} />
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {po.supplierName}
                    {po.supplierCode ? ` (${po.supplierCode})` : ""} · Hub{" "}
                    {po.branchCode} · {formatLakDisplay(po.subtotalLak)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => nav({ to: "/app/inventory/purchase-orders" })}
                  >
                    <ArrowLeftIcon className="size-4" />
                    ກັບຄືນ
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      try {
                        openPurchaseOrderPrint(po);
                      } catch (e) {
                        const msg =
                          e instanceof Error && e.message === "POPUP_BLOCKED"
                            ? "ເປີດ popup ຖືກບລັອກ — ອະນຸຍາດ popup ແລ້ວລອງໃໝ່"
                            : "ພິມລົ້ມເຫຼວ";
                        toast.error(msg);
                      }
                    }}
                  >
                    <PrinterIcon className="size-4" />
                    ພິມ / PDF
                  </Button>
                  {po.status === "DRAFT" && canCreate && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() =>
                          nav({
                            to: "/app/inventory/purchase-orders/$id/edit",
                            params: { id },
                          })
                        }
                      >
                        <PencilIcon className="size-4" />
                        ແກ້ໄຂ
                      </Button>
                      <Button
                        onClick={() =>
                          runWithConfirm(
                            {
                              title: "ສົ່ງ PO ເພື່ອອະນຸມັດ?",
                              description:
                                "ຫຼັງສົ່ງຈະແກ້ບັນທັດບໍ່ໄດ້ ຈົນກວ່າ HQ ຈະປະຕິເສດກັບເປັນຮ່າງ",
                              actionText: "ສົ່ງອະນຸມັດ",
                            },
                            "ສົ່ງອະນຸມັດ",
                            () => submit.mutateAsync(),
                          )
                        }
                        isLoading={submit.isPending}
                      >
                        <SendIcon className="size-4" />
                        ສົ່ງອະນຸມັດ
                      </Button>
                    </>
                  )}
                  {po.status === "SUBMITTED" && canApprove && (
                    <>
                      <Button
                        variant="destructive"
                        onClick={() =>
                          runWithConfirm(
                            {
                              title: "ປະຕິເສດ PO?",
                              description:
                                "PO ຈະກັບໄປສະຖານະຮ່າງ (DRAFT) ແລະ ສາມາດແກ້ໄຂໄດ້ອີກ",
                              actionText: "ປະຕິເສດ",
                              ActionProps: { variant: "destructive" },
                            },
                            "ປະຕິເສດ",
                            () => reject.mutateAsync(),
                          )
                        }
                        isLoading={reject.isPending}
                      >
                        <XIcon className="size-4" />
                        ປະຕິເສດ
                      </Button>
                      <Button
                        onClick={() =>
                          runWithConfirm(
                            {
                              title: "ອະນຸມັດ PO?",
                              description:
                                "ຫຼັງອະນຸມັດສາຂາ Hub ສາມາດຮັບເຂົ້າ lot ຕາມ PO ໄດ້",
                              actionText: "ອະນຸມັດ",
                            },
                            "ອະນຸມັດ",
                            () => approve.mutateAsync(),
                          )
                        }
                        isLoading={approve.isPending}
                      >
                        <CheckIcon className="size-4" />
                        ອະນຸມັດ
                      </Button>
                    </>
                  )}
                  {canReceiveNow && canReceive && (
                    <Button onClick={() => setReceiveOpen(true)}>
                      <PackageIcon className="size-4" />
                      ຮັບເຂົ້າ
                    </Button>
                  )}
                  {(po.status === "SUBMITTED" ||
                    po.status === "APPROVED" ||
                    po.status === "PARTIALLY_RECEIVED") &&
                    canApprove && (
                      <Button
                        variant="outline"
                        onClick={() =>
                          runWithConfirm(
                            {
                              title: "ຍົກເລີກ PO?",
                              description:
                                "PO ຈະຖືກຍົກເລີກ — ບໍ່ສາມາດຮັບເຂົ້າຫຼືແກ້ໄຂຕໍ່",
                              actionText: "ຍົກເລີກ PO",
                              ActionProps: { variant: "destructive" },
                            },
                            "ຍົກເລີກ",
                            () => cancel.mutateAsync(),
                          )
                        }
                        isLoading={cancel.isPending}
                      >
                        ຍົກເລີກ PO
                      </Button>
                    )}
                  {(po.status === "PARTIALLY_RECEIVED" ||
                    po.status === "APPROVED") &&
                    canApprove && (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          runWithConfirm(
                            {
                              title: "ປິດ PO?",
                              description:
                                "ປິດງານແມ້ຍັງຮັບບໍ່ຄົບ — ປະລິມານຄ້າງຈະບໍ່ຮັບຕໍ່",
                              actionText: "ປິດ PO",
                            },
                            "ປິດ PO",
                            () => close.mutateAsync(),
                          )
                        }
                        isLoading={close.isPending}
                      >
                        ປິດ PO
                      </Button>
                    )}
                </div>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">ຂໍ້ມູນຫົວ</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <p className="text-muted-foreground text-xs">ວັນທີສັ່ງ</p>
                    <p className="font-medium">{formatDateLocal(po.orderDate)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">ຄາດວ່າຮັບ</p>
                    <p className="font-medium">
                      {po.expectedDate ? formatDateLocal(po.expectedDate) : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">ສາຂາ Hub</p>
                    <p className="font-medium">
                      {po.branchName} ({po.branchCode})
                    </p>
                  </div>
                  {po.note && (
                    <div className="sm:col-span-2">
                      <p className="text-muted-foreground text-xs">ໝາຍເຫດ</p>
                      <p>{po.note}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Tabs defaultValue="lines">
                <TabsList>
                  <TabsTrigger value="lines">ບັນທັດ PO</TabsTrigger>
                  <TabsTrigger value="receipts">
                    ປະຫວັດຮັບ ({po.receipts.length})
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="lines" className="mt-4">
                  <Card>
                    <CardContent className="pt-6">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>#</TableHead>
                            <TableHead>ລາຍການ</TableHead>
                            <TableHead>ປະເພດ</TableHead>
                            <TableHead className="text-right">ສັ່ງ</TableHead>
                            <TableHead className="text-right">ຮັບແລ້ວ</TableHead>
                            <TableHead className="text-right">ຄ້າງ</TableHead>
                            <TableHead className="text-right">ລາຄາ</TableHead>
                            <TableHead className="text-right">ລວມ</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {po.lines.map((line) => (
                            <TableRow key={line.id}>
                              <TableCell>{line.lineNo}</TableCell>
                              <TableCell>{line.itemName}</TableCell>
                              <TableCell>
                                {poItemTypeLabel(line.itemType)}
                              </TableCell>
                              <TableCell className="text-right">
                                <div>
                                  {formatQtyDisplay(line.qtyOrdered)} {line.unit}
                                </div>
                                {line.toBaseFactor !== 1 ? (
                                  <div className="text-muted-foreground text-xs">
                                    = {formatQtyDisplay(line.qtyOrdered * line.toBaseFactor)}{" "}
                                    {line.baseUnit}
                                  </div>
                                ) : null}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatQtyDisplay(line.qtyReceived)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatQtyDisplay(line.qtyOpen)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatLakDisplay(line.unitCostLak)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatLakDisplay(line.lineTotalLak)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>
                <TabsContent value="receipts" className="mt-4">
                  <Card>
                    <CardContent className="pt-6">
                      {po.receipts.length === 0 ? (
                        <p className="text-muted-foreground text-sm">
                          ຍັງບໍ່ມີປະຫວັດການຮັບ — ໃຊ້ປຸ່ມ «ຮັບເຂົ້າ» ເມື່ອ PO
                          ອະນຸມັດແລ້ວ
                        </p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>ເລກ GRN</TableHead>
                              <TableHead>ວັນທີຮັບ</TableHead>
                              <TableHead>ບັນທັດ</TableHead>
                              <TableHead>Lot</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {po.receipts.map((r) => (
                              <TableRow key={r.id}>
                                <TableCell className="font-mono">
                                  {r.receiptNo}
                                </TableCell>
                                <TableCell>
                                  {formatDateTimeLocal(r.receivedAt)}
                                </TableCell>
                                <TableCell>{r.lineCount}</TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-2">
                                    {r.lots.length === 0 ? (
                                      <span className="text-muted-foreground">
                                        —
                                      </span>
                                    ) : (
                                      r.lots.map((lot) => (
                                        <Link
                                          key={lot.lotId}
                                          to="/app/inventory/lots/$id/movements"
                                          params={{ id: lot.lotId }}
                                          className="text-primary text-sm underline-offset-2 hover:underline"
                                        >
                                          {lot.lotNo}
                                        </Link>
                                      ))
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              {receiveOpen && (
                <PurchaseOrderReceiveDialog
                  open={receiveOpen}
                  onOpenChange={setReceiveOpen}
                  po={po}
                  isPending={receive.isPending}
                  onConfirm={async (lines) => {
                    try {
                      await receive.mutateAsync({ lines });
                      toast.success("ຮັບເຂົ້າສຳເລັດ");
                      setReceiveOpen(false);
                    } catch (e) {
                      toast.error(formatPurchaseOrderApiError(e));
                    }
                  }}
                />
              )}
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
